/**
 * TRUS-709: who owns a member conversation and who has been answering it.
 * Replaces the single-assignee DMAssignmentBar.
 *
 *  - Owners (0..n), shown the moment the thread opens (from the Inbox row,
 *    then the channel detail): Add (several at once), Replace, Remove, and a
 *    quick "Assign to me".
 *  - Responders: the staff who replied, with their last reply time — in orgs
 *    where several people answer the same member, everyone can see who did.
 *
 * Owner changes go to POST/DELETE /api/messaging/channels/{id}/owners. On an
 * older backend (404) Add / Replace fall back to the single-assignee
 * dm-management/assign call; Remove reports that it is not available yet.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, UserPlus, Users, X } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { messagingApi, type CaseManagerWorkload, type Channel } from '@/services/messagingApi';
import {
  isMissingEndpoint,
  memberInboxApi,
  normalizeResponders,
  normalizeStaffRefs,
  ownerErrorCode,
  type OwnersMode,
  type Responder,
  type StaffRef,
} from '@/services/memberInboxApi';
import { getCurrentUserId } from '@/utils/auth';
import { MEMBER_INBOX_KEY, initialsOf, relativeAgo } from './memberInbox';

type ChannelWithOwners = Channel & { owners?: unknown; responders?: unknown };

export function ownersOf(channel: ChannelWithOwners): StaffRef[] | null {
  return Array.isArray(channel.owners) ? normalizeStaffRefs(channel.owners) : null;
}

export function respondersOf(channel: ChannelWithOwners): Responder[] {
  return normalizeResponders(channel.responders).slice(0, 5);
}

interface OwnerPickerState {
  mode: OwnersMode;
}

export function ConversationOwnersBar({ channel }: { channel: ChannelWithOwners }) {
  const { t } = useAppTranslation();
  const qc = useQueryClient();
  const myUserId = getCurrentUserId();
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;

  const serverOwners = ownersOf(channel);
  // A change made here shows at once, until the refreshed channel catches up.
  const [localOwners, setLocalOwners] = useState<StaffRef[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picker, setPicker] = useState<OwnerPickerState | null>(null);
  const serverKey = JSON.stringify(serverOwners);
  useEffect(() => {
    setLocalOwners(null);
  }, [channel.id, serverKey]);
  useEffect(() => {
    setError(null);
    setPicker(null);
  }, [channel.id]);

  const owners = localOwners ?? serverOwners ?? [];
  const ownersKnown = localOwners != null || serverOwners != null;
  const responders = respondersOf(channel);
  const iOwn = myUserId != null && owners.some((o) => o.user_id === myUserId);

  const { data: staff = [] } = useQuery<CaseManagerWorkload[]>({
    queryKey: ['dm-available-staff', cboId],
    queryFn: () => messagingApi.getAvailableStaff(),
    enabled: picker != null || !iOwn,
    staleTime: 60_000,
  });
  const staffName = (id: number) => staff.find((s) => s.user_id === id)?.user_name || '';

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['channel-detail', channel.id] });
    qc.invalidateQueries({ queryKey: [MEMBER_INBOX_KEY] });
    qc.invalidateQueries({ queryKey: ['messaging-channels'] });
    qc.invalidateQueries({ queryKey: ['dm-channels'] });
  };

  const explain = (err: unknown, ids: number[]) => {
    if (ownerErrorCode(err) === 'owner_cannot_see_member') {
      const names = ids.map(staffName).filter(Boolean).join(', ');
      return t(
        'messaging.owners.cannotSeeMember',
        "{{names}} can't see this member under your organization's caseload rules.",
        { names: names || t('messaging.owners.thatPerson', 'That person') },
      );
    }
    return t('messaging.owners.saveFailed', 'Could not update owners.');
  };

  const setOwners = useMutation({
    mutationFn: async ({ ids, mode }: { ids: number[]; mode: OwnersMode }) => {
      try {
        return { owners: await memberInboxApi.setOwners(channel.id, ids, mode), legacy: false };
      } catch (err) {
        if (!isMissingEndpoint(err) || ids.length === 0) throw err;
        // Older backend: one assignee per conversation.
        await messagingApi.assignChannels({
          channel_ids: [channel.id],
          assign_to_user_id: ids[0],
          assignment_type: 'reassignment',
        });
        return { owners: null, legacy: true };
      }
    },
    onSuccess: (res, { ids, mode }) => {
      setError(null);
      setPicker(null);
      const picked = ids.map((id) => ({ user_id: id, name: staffName(id) || (id === myUserId ? t('messaging.assign.you', 'you') : '') }));
      if (res.owners) setLocalOwners(res.owners);
      else if (res.legacy) setLocalOwners(picked.slice(0, 1));
      else if (mode === 'replace') setLocalOwners(picked);
      else {
        const merged = [...owners];
        for (const p of picked) if (!merged.some((o) => o.user_id === p.user_id)) merged.push(p);
        setLocalOwners(merged);
      }
      refresh();
    },
    onError: (err, { ids }) => setError(explain(err, ids)),
  });

  const removeOwner = useMutation({
    mutationFn: (userId: number) => memberInboxApi.removeOwner(channel.id, userId),
    onSuccess: (_res, userId) => {
      setError(null);
      setLocalOwners(owners.filter((o) => o.user_id !== userId));
      refresh();
    },
    onError: (err) =>
      setError(
        isMissingEndpoint(err)
          ? t('messaging.owners.removeUnavailable', 'Removing an owner is not available yet.')
          : t('messaging.owners.saveFailed', 'Could not update owners.'),
      ),
  });

  const busy = setOwners.isPending || removeOwner.isPending;

  return (
    <div
      className="flex flex-col gap-1.5 border-b bg-indigo-50 px-3 py-2 text-sm dark:bg-indigo-950/30 sm:px-4"
      data-testid="conversation-owners-bar"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="flex items-center gap-1.5 font-medium text-indigo-900 dark:text-indigo-200">
          <Users className="h-4 w-4" aria-hidden="true" />
          {t('messaging.owners.label', 'Owners')}
        </span>
        <ul className="flex min-w-0 flex-wrap items-center gap-1.5" data-testid="conversation-owners">
          {owners.map((o) => (
            <li
              key={o.user_id}
              className="flex items-center gap-1 rounded-full border border-indigo-200 bg-white py-0.5 pl-0.5 pr-1.5 text-xs dark:border-indigo-800 dark:bg-slate-900"
              data-testid={`owner-chip-${o.user_id}`}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[9px] font-semibold text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200">
                {initialsOf(o.name)}
              </span>
              <span className="max-w-[10rem] truncate">
                {o.user_id === myUserId ? t('messaging.owners.youName', '{{name}} (you)', { name: o.name || t('messaging.assign.you', 'you') }) : o.name || t('messaging.sender.staff', 'Staff')}
              </span>
              <button
                type="button"
                className="rounded-full p-0.5 text-muted-foreground hover:bg-indigo-100 hover:text-foreground disabled:opacity-50 dark:hover:bg-indigo-900"
                onClick={() => removeOwner.mutate(o.user_id)}
                disabled={busy}
                aria-label={t('messaging.owners.remove', 'Remove {{name}} as owner', { name: o.name })}
                title={t('messaging.owners.remove', 'Remove {{name}} as owner', { name: o.name })}
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
          {ownersKnown && owners.length === 0 && (
            <li className="text-xs text-indigo-900/70 dark:text-indigo-200/70" data-testid="conversation-no-owner">
              {t('messaging.owners.none', 'No owner yet')}
            </li>
          )}
        </ul>
        <div className="ml-auto flex flex-shrink-0 items-center gap-1.5">
          {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!iOwn && myUserId != null && (
            <Button
              size="sm"
              variant="outline"
              className="h-7"
              disabled={busy}
              onClick={() => setOwners.mutate({ ids: [myUserId], mode: 'add' })}
              data-testid="owners-assign-me"
            >
              {t('messaging.assign.assignToMe', 'Assign to me')}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1"
            disabled={busy}
            onClick={() => setPicker({ mode: 'add' })}
            data-testid="owners-add"
          >
            <UserPlus className="h-3.5 w-3.5" />
            {t('messaging.owners.add', 'Add')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            disabled={busy}
            onClick={() => setPicker({ mode: 'replace' })}
            data-testid="owners-replace"
          >
            {t('messaging.owners.replace', 'Replace')}
          </Button>
        </div>
      </div>

      {responders.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid="conversation-responders">
          <span className="font-medium text-indigo-900 dark:text-indigo-200">
            {t('messaging.responders.label', 'Replied')}
          </span>
          {responders.map((r) => (
            <span
              key={r.user_id}
              className="flex items-center gap-1 text-indigo-900/80 dark:text-indigo-100/80"
              data-testid={`responder-${r.user_id}`}
              title={r.last_replied_at || undefined}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-[9px] font-semibold text-indigo-700 ring-1 ring-indigo-200 dark:bg-slate-900 dark:text-indigo-200 dark:ring-indigo-800">
                {initialsOf(r.name)}
              </span>
              {t('messaging.responders.entry', '{{name}} · {{time}}', {
                name: r.name || t('messaging.sender.staff', 'Staff'),
                time: relativeAgo(r.last_replied_at, t),
              })}
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="flex items-start justify-between gap-2 text-xs text-destructive" role="alert" data-testid="owners-error">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label={t('messaging.owners.dismiss', 'Dismiss')}>
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {picker && (
        <OwnerPickerDialog
          mode={picker.mode}
          staff={staff}
          currentOwnerIds={owners.map((o) => o.user_id)}
          pending={setOwners.isPending}
          onCancel={() => setPicker(null)}
          onConfirm={(ids) => setOwners.mutate({ ids, mode: picker.mode })}
        />
      )}
    </div>
  );
}

function OwnerPickerDialog({
  mode,
  staff,
  currentOwnerIds,
  pending,
  onCancel,
  onConfirm,
}: {
  mode: OwnersMode;
  staff: CaseManagerWorkload[];
  currentOwnerIds: number[];
  pending: boolean;
  onCancel: () => void;
  onConfirm: (ids: number[]) => void;
}) {
  const { t } = useAppTranslation();
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<number[]>([]);
  const candidates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return staff
      .filter((s) => (mode === 'add' ? !currentOwnerIds.includes(s.user_id) : true))
      .filter((s) =>
        needle ? `${s.user_name || ''} ${s.email || ''}`.toLowerCase().includes(needle) : true,
      );
  }, [staff, query, mode, currentOwnerIds]);
  const toggle = (id: number) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add'
              ? t('messaging.owners.addTitle', 'Add owners')
              : t('messaging.owners.replaceTitle', 'Replace owners')}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add'
              ? t('messaging.owners.addHint', 'Pick one or more staff to own this conversation with the current owners.')
              : t('messaging.owners.replaceHint', 'The people you pick become the only owners. Nobody loses their unread count or pins.')}
          </DialogDescription>
        </DialogHeader>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('messaging.owners.search', 'Search staff…')}
          aria-label={t('messaging.owners.search', 'Search staff…')}
        />
        <div className="max-h-72 overflow-y-auto rounded-md border" role="group" aria-label={t('messaging.owners.staffList', 'Staff')}>
          {candidates.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t('messaging.assign.noStaff', 'No staff found')}
            </div>
          ) : (
            candidates.map((s) => (
              <label
                key={s.user_id}
                className="flex cursor-pointer items-center gap-2 border-b px-3 py-2 text-sm last:border-b-0 hover:bg-muted/50"
              >
                <input
                  type="checkbox"
                  checked={picked.includes(s.user_id)}
                  onChange={() => toggle(s.user_id)}
                  data-testid={`owner-option-${s.user_id}`}
                />
                <span className="min-w-0 flex-1 truncate">{s.user_name || s.email}</span>
                {currentOwnerIds.includes(s.user_id) && (
                  <span className="text-xs text-muted-foreground">{t('messaging.owners.currentOwner', 'owner')}</span>
                )}
              </label>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={pending}>
            {t('messaging.channel.cancel', 'Cancel')}
          </Button>
          <Button onClick={() => onConfirm(picked)} disabled={pending || picked.length === 0} data-testid="owners-confirm">
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'add'
              ? t('messaging.owners.addConfirm', 'Add owners ({{count}})', { count: picked.length })
              : t('messaging.owners.replaceConfirm', 'Replace owners ({{count}})', { count: picked.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConversationOwnersBar;
